import React, { useState, useEffect } from "react";
import { getRequest, delRequest } from "../../axiosRoutes/index";
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Pagination,
  Tooltip,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
} from "@mui/material";
import SideNav from "../sidebar/SideNav";
import HrSideNavBar from "../sidebar/HrSideNavBar";
import EditIcon from "@mui/icons-material/Edit";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "../../axiosRoutes/snackbarConfig";
import AddInventory from "./AddInventory";
import AssignItem from "./AssignItem";
import { useInView } from "react-intersection-observer";

const Inventory = () => {
  const { snackbar, showSnackbar, handleSnackbarClose } = useSnackbar();
  const [hover, setHover] = useState(null);
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const [inventories, setInventories] = useState([]);
  const [assignData, setAssignData] = useState([]);
  const [openItem, setOpenItem] = useState(false);
  const [unassignData, setUnassignData] = useState({
    userId: "",
  });
  const [itemData, setItemData] = useState({
    title: "",
    price: 0,
    category: "",
    condition: "",
    quantity: 0,
    purchaseDate: "",
    propertiesList: [
      {
        id: "",
        properties: [],
      },
    ],
    assignedTo: [
      {
        userId: "",
        name: "",
        quantity: 0,
      }
    ]
  });
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [openAssign, setOpenAssign] = useState(false);
  const [openUnassign, setOpenUnassign] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updateId, setUpdateId] = useState(null);
  const [itemId, setItemId] = useState(null);
  const role = localStorage.getItem("role");
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(10)
  const [isFetchingInventories, setIsFetchingInventories] = useState(false)
  const { ref, inView } = useInView({
    threshold: 0,
  });
  const [limit, setLimit] = useState(10)
  const [conditions, setConditions] = useState([])

  useEffect(() => {
      if (inView) {
          if (limit < totalPages * 10) {
            setLimit((prev) => prev + 10);
          }
        }
  }, [inView]);

  const fetchConditions = async() =>{
    const access = localStorage.getItem("access");
    try{
      const response = await getRequest('filter/condition', {
        headers: {
          Authorization: `Bearer ${access}`,
        }
      })
      const conditionsArray = Object.keys(response).map((key)=>({
        label: key,
        value: response[key],
      }))
      setConditions(conditionsArray)
    }catch(error){
      console.error("Error Fetching Inventories:", error);
    }
  }

  const fetchInventories = async () => {
    const access = localStorage.getItem("access");
    try {
      setIsFetchingInventories(true)
      !isSmallScreen && setLoading(true)
      const response = await getRequest("admin/inventory", {
        headers: {
          Authorization: `Bearer ${access}`,
        },
        params: !isSmallScreen ? {page:page} : {limit: limit}
      });
      setIsFetchingInventories(false)
      setTotalPages(response?.totals?.total_pages)
      const formattedInventories = response?.data.map((inventory)=>{
        const conditionObj = conditions.find((condition) => condition.value === inventory.condition)
        inventory.condition = conditionObj ? conditionObj.label : inventory.condition
      })
      setInventories(response?.data)
    } catch (error) {
      console.error("Error Fetching Inventories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=>{
    fetchInventories()
  }, [conditions])

  useEffect(() => {
    fetchConditions()
  }, [page, limit]);

  const handleDelete = async () => {
    try {
      const access = localStorage.getItem("access");
      const response = await delRequest(`admin/inventory/${deleteId}`, access);
      fetchInventories();
      handleClose();
      showSnackbar(response, "success");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleOpen = () => {
    setOpenModal(true);
  };

  const handleAssignOpen = () => {
    setOpenAssign(true);
  };

  const handleUnassignOpen = (id) => {
    setItemId(id);
    setOpenUnassign(true);
  };

  const handleClickOpen = (id) => {
    setDeleteId(id);
    setOpen(true);
  };

  const selectInventory = (id) => {
    setUpdateId(id);
    handleOpen();
  };

  const handleAssign = (id) => {
    setItemId(id);
    handleAssignOpen();
  };

  const handleSelectChange = (event) => {
    const selectedUserId = event.target.value;
    setUnassignData((prevState) => ({
      ...prevState,
      userId: selectedUserId,
    }));
  };

  const handleUnassign = async () => {
    try {
      const access = localStorage.getItem("access");
      const response = await delRequest(
        `admin/inventory/${itemId}/assignTo`,
        access,
        unassignData
      );
      fetchInventories();
      handleClose();
      showSnackbar(response, "success");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleItem = (id) => {
    setOpenItem(true);
    setItemId(id);
  };

  useEffect(() => {
    if (itemId !== null) {
      const fetchAssign = async () => {
        const access = localStorage.getItem("access");
        try {
          const res = await getRequest(`admin/inventory/${itemId}`, {
            headers: {
              Authorization: `Bearer ${access}`,
            },
          });
          const assignedData = res.assignedTo.map((user) => ({
            userId: user.userId,
            name: user.name,
          }));
          const date = new Date(res.purchaseDate).toISOString().split("T")[0];
          let returnCondition = () => {
            return conditions.find(
                        (condition) => condition.value === res.condition
                    )?.label
          }
          setItemData((prevData) => ({
            ...prevData,
            title: res.title,
            price: res.price,
            category: res.category,
            condition: returnCondition(),
            quantity: res.quantity,
            purchaseDate: date,
            propertiesList: Array.isArray(res.propertiesList)
              ? res.propertiesList.map((item) => ({
                id: item.id || "",
                properties: item.properties || [],
              }))
              : [{ id: "", properties: [] }],
            assignedTo: Array.isArray(res.assignedTo)
              ? res.assignedTo.map((item) => ({
                userId: item.userId || "",
                name: item.name,
                quantity: item.quantity || 0,
              }))
              : [{ userId: "", name: "", quantity: 0, }],
          }));
          setAssignData(assignedData);
        } catch (error) {
          console.error("Error fetching Item:", error);
        }
      };
      fetchAssign();
    }
  }, [itemId]);

  const handleClose = () => {
    setOpenModal(false);
    setOpen(false);
    setOpenAssign(false);
    setOpenItem(false);
    setOpenUnassign(false);
    fetchInventories();
    setItemId(null);
    setAssignData([]);
    setDeleteId(null);
    setUpdateId(null);
  };

  const headingTableCellStyle =  {
    color: "#fff",
    textAlign: "center",
    borderBottom: "2px solid #1F1F1F",
    fontSize: { xs: "0.8rem", sm: "1rem" },
    backgroundColor: '#1F1F1F'
  }

  const dataTableCellStyle = (hover, id) => {
    return {
      color: hover === id ? "#FF5722" : "#fff",
      textAlign: "center",
      borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
      fontSize: { xs: "0.8rem", sm: "1rem" },
      padding: '10px 16px',
      backgroundColor: '#292929'
    };
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        textAlign="center"
      >
        <CircularProgress size={50} sx={{ color: "#FF5722" }} />
        <Typography variant="h6" sx={{ marginTop: "18px", color: "#fff" }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <div className="app-container" style={{ color: "#fff", display: "flex" }}>
        {role === "admin" && <SideNav />}
        {role === "hr" && <HrSideNavBar />}
        <div style={{ flex: 1, margin: `${isSmallScreen ? '3.1rem' : '0.5rem'} 0.5rem 0.5rem 0.5rem` }}>
          <Box sx={{ backgroundColor: "#000", marginTop: { xs: 1, md: 0 } }}>
            <Grid
              container
              sx={{
                alignItems: "center",
                justifyContent: { xs: "center", md: "space-between" },
                flexDirection: { xs: "column", md: "row" },
                padding: "1rem",
              }}
            >
              <Grid item>
                <h1>Office Inventory</h1>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={handleOpen}
                  sx={{
                    backgroundColor: "#fff",
                    color: "black",
                    "&:hover": {
                      backgroundColor: "#e1e1e1",
                    },
                  }}
                >
                  Add Items
                </Button>
              </Grid>
            </Grid>
            {inventories.length === 0 ? (
              <Typography variant="h6" sx={{ color: "#fff", textAlign: "center" }}>
                No Data Yet
              </Typography>
            ) : (
              <>
                {!isSmallScreen ? (
                  <TableContainer sx={{ overflowX: "auto", maxWidth: "100vw" }}>
                    <Table
                      style={{
                        borderRadius: "25px",
                        borderCollapse: "separate",
                        overflow: "hidden",
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell style={headingTableCellStyle}>
                            ID
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Title
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Condition
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Price
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Properties ID
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Properties List
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Quantity
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Remaining Quantity
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Assigned To
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inventories.map((inventory, id) => (
                          <TableRow
                            key={id}
                            style={{
                              cursor: "pointer",
                            }}
                            onMouseEnter={() => setHover(id)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => handleItem(inventory._id)}
                          >
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {(page - 1) * 10 + id + 1}
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {inventory.title}
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {inventory.condition}
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {inventory.price}
                            </TableCell>
                            <TableCell
                              style={{
                                color: hover === id ? "#FF5722" : "#fff",
                                textAlign: "center",
                                borderBottom:"1px solid rgba(255, 255, 255, 0.2)",
                                fontSize: { xs: "0.8rem", sm: "1rem" },
                                padding: "10px 16px",
                                backgroundColor: "#292929",
                                maxWidth: '200px'
                              }}
                            >
                              {inventory.propertiesList.map(
                                (property) => `${property.id}`
                              )}
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {inventory.propertiesList.map(
                                (property) => `${property.properties}, `
                              )}
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {inventory.quantity}
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {inventory.quantity -
                                inventory.assignedTo.reduce(
                                  (total, assign) => total + assign.quantity,
                                  0
                                )}
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              <ul
                                style={{
                                  textAlign: "left",
                                  padding: "0px auto",
                                }}
                              >
                                {inventory.assignedTo.map((assign, keys) => (
                                  <li key={keys}>{assign.name}</li>
                                ))}
                              </ul>
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              <Tooltip title="Assign" placement="top">
                                <IconButton
                                  style={{
                                    color: "#fff",
                                    backgroundColor: "#1976D2",
                                    margin: "5px",
                                  }}
                                  aria-label="edit"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAssign(inventory._id)
                                  }}
                                >
                                  <InventoryOutlinedIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="UnAssign" placement="top">
                                <IconButton
                                  style={{
                                    color: "#fff",
                                    backgroundColor: "#F44336",
                                    margin: "5px",
                                  }}
                                  aria-label="edit"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnassignOpen(inventory._id)
                                  }}
                                >
                                  <DeleteSweepIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#1976D2",
                                  margin: "5px",
                                }}
                                aria-label="view"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleItem(inventory._id)
                                }}
                              >
                                <VisibilityIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#4CAF50",
                                  margin: "5px",
                                }}
                                aria-label="edit"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectInventory(inventory._id)
                                }}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#F44336",
                                  margin: "5px",
                                }}
                                aria-label="delete"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickOpen(inventory._id)
                                }}
                              >
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter style={{ backgroundColor: "#1F1F1F" }}>
                        <TableRow>
                          <TableCell
                            colSpan={100}
                            style={{
                              color: "white",
                              border: "none",
                              textAlign: "center",
                            }}
                          >
                            <Pagination
                              count={totalPages}
                              variant="outlined"
                              color="primary"
                              page={page}
                              onChange={(e, value) => {
                                setPage(value);
                              }}
                              sx={{
                                display: "inline-block",
                                "& .MuiPaginationItem-root": {
                                  color: "#fff",
                                  borderColor: "#fff",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    backgroundColor: "#FF5722",
                                    color: "#fff",
                                    borderColor: "#FF5722",
                                  },
                                  "&.Mui-selected": {
                                    backgroundColor: "#FF5722",
                                    color: "#fff",
                                    borderColor: "#FF5722",
                                  },
                                },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                ) : (
                  <Grid container spacing={1} justifyContent='center'>
                    {inventories.map((inventory, id) => (
                      <Grid item xs={12} key={id}>
                        <Card ref = {id+1 ===inventories.length ? ref : null}
                          sx={{
                            backgroundColor: "#101010",
                            color: "#fff",
                          }}
                          onMouseEnter={() => setHover(id)}
                          onMouseLeave={() => setHover(null)}
                          onClick={() => handleItem(inventory._id)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <CardContent>
                              <Typography variant="h6">
                                {inventory.title}
                              </Typography>
                              <Typography variant="body2">
                                Condition: {inventory.condition}
                                <br />
                                Properties ID: {inventory.propertiesList.map(
                                  (property) => `${property.id}`
                                )}
                              </Typography>
                            </CardContent>
                            <CardActions>
                              <Tooltip title="Assign" placement="top">
                                <IconButton
                                  style={{
                                    color: "#fff",
                                    backgroundColor: "#1976D2",
                                    margin: "2px",
                                  }}
                                  aria-label="edit"
                                  size="medium"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAssign(inventory._id)}}
                                >
                                  <InventoryOutlinedIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="UnAssign" placement="top">
                                <IconButton
                                  style={{
                                    color: "#fff",
                                    backgroundColor: "#F44336",
                                    margin: "2px",
                                  }}
                                  aria-label="edit"
                                  size="medium"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnassignOpen(inventory._id)}}
                                >
                                  <DeleteSweepIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#1976D2",
                                  margin: "2px",
                                }}
                                aria-label="view"
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleItem(inventory._id)}}
                              >
                                <VisibilityIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#4CAF50",
                                  margin: "2px",
                                }}
                                aria-label="edit"
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectInventory(inventory._id)}}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#F44336",
                                  margin: "2px",
                                }}
                                aria-label="delete"
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickOpen(inventory._id)}}
                              >
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </CardActions>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </Box>

          <Dialog
            open={openItem}
            maxWidth
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#212121",
                opacity: 0.9,
                color: "#fff",
                padding: "5px",
                maxWidth: { xs: "90%", md: "50%" },
                border: "2px solid #D0521B",
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "22px",
                fontWeight: "bold",
              }}
            >
              {itemData.title}
              <IconButton
                onClick={handleClose}
                sx={{
                  color: "#fff",
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Grid container justifyContent="space-between" alignItems="center" sx={{ borderTop: "2px solid #D0521B", paddingTop: "1rem", }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    <b>Title:</b> {itemData.title}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    <b>Condition:</b> {itemData.condition}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    <b>Price:</b> {itemData.price}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    <b>Properties ID:</b> {itemData.propertiesList.map(
                      (property) => `${property.id}`
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    <b>Amount:</b> {itemData.propertiesList.map((property) => `${property.properties}, `)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    <b>Quantity:</b> {itemData.quantity}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    <b>Remaining Quantity:</b> {itemData.quantity - itemData.assignedTo.reduce((total, assign) => total + assign.quantity, 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    <b>Assigned To:</b> {itemData.assignedTo.map((assign, keys) => (`${assign.name}, `))}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
          </Dialog>
          <Dialog
            open={open}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#212121",
                color: "#fff",
                padding: "15px",
              },
              "& .MuiDialogTitle-root": {
                padding: "5px",
              },
              "& .MuiDialogContentText-root": {
                color: "#fff",
              },
              "& .MuiDialogContent-root": {
                padding: "5px",
              },
              "& .MuiDialogActions-root": {
                padding: "5px",
              },
            }}
          >
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are You Sure You Want to Delete this Item?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                sx={{
                  backgroundColor: "#A6A6A6",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#B8B8B8" },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                sx={{
                  backgroundColor: "#E5622D",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#D0521B" },
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            maxWidth="sm"
            open={openUnassign}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#212121",
                color: "#fff",
                padding: "20px",
                borderRadius: "8px",
              },
              "& .MuiDialogTitle-root": {
                padding: "10px 5px",
                borderBottom: "1px solid #E5622D",
              },
              "& .MuiDialogContent-root": {
                padding: "10px",
              },
              "& .MuiDialogActions-root": {
                padding: "10px 5px",
                justifyContent: "space-around",
              },
            }}
          >
            <DialogTitle
              sx={{
                typography: "h6",
                marginBottom: "10px",
                fontWeight: "bold",
                color: "#E5622D",
              }}
            >
              Confirm UnAssign Item
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ marginBottom: "20px", color: "#fff" }}>
                Are you sure you want to unassign this item from the selected
                employee?
              </DialogContentText>
              {assignData.length === 0 ? (
                <Typography
                  variant="h6"
                  sx={{ color: "#fff", textAlign: "center", margin: "10px 0" }}
                >
                  No Item Assigned Yet
                </Typography>
              ) : (
                <FormControl
                  variant="outlined"
                  fullWidth
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      color: "#FEFEFE",
                      "& fieldset": {
                        borderColor: "#FEFEFE",
                      },
                      "&:hover fieldset": {
                        borderColor: "#FEFEFE",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#FEFEFE",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#FEFEFE",
                    },
                  }}
                >
                  <InputLabel id="client-select-label">Employee Name</InputLabel>
                  <Select
                    labelId="client-select-label"
                    value={unassignData.userId}
                    onChange={handleSelectChange}
                    label="Employee Name"
                  >
                    {assignData.map((assign) => (
                      <MenuItem key={assign.userId} value={assign.userId}>
                        {assign.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                sx={{
                  backgroundColor: "#A6A6A6",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#B8B8B8" },
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={assignData.length === 0}
                onClick={handleUnassign}
                sx={{
                  backgroundColor: "#E5622D",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#D0521B" },
                  "&.Mui-disabled": {
                    backgroundColor: "#EC916C",
                    color: "#fff",
                  },
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          <AddInventory
            open={openModal}
            handleClose={handleClose}
            inventoryId={updateId}
          />
          <AssignItem
            open={openAssign}
            handleClose={handleClose}
            itemId={itemId}
          />
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isSmallScreen && <Grid container width='100%' height='10vh' alignItems='center' justifyContent='center' style={{position: 'fixed', bottom: (isFetchingInventories ? '20px' : '-100px'), transition: 'bottom 0.5s ease'}}>
          <CircularProgress size="3rem" sx={{color: "#FF5722"}} />
        </Grid>}
    </>
  );
};

export default Inventory;
